import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTrans, useTrapKeyboardNavigation } from "../../../hooks";
import { Button, ButtonSize } from "@wattpad/web-ui-library";
import { Icon } from "../../../shared-components";

// TODO: Once the Mobile Prompt Redesign is launced, remove "New" prefix and delete old MobileDownloadPrompt
const MobileDownloadPromptNew = ({ onSendPromptEvent, getAppUrl }) => {
  const { trans } = useTrans();
  const [isVisible, setIsVisible] = useState(false);
  const [drawerRef, setDrawerRef] = useState();
  const drawerRefCb = useCallback(
    ref => {
      setDrawerRef(ref);
    },
    [setDrawerRef]
  );
  useTrapKeyboardNavigation(drawerRef);

  useEffect(
    () => {
      if (shouldShowPrompt()) {
        onSendPromptEvent("view");
        document.body.classList.add("modal-open");
        setIsVisible(true);
      }
    },
    [onSendPromptEvent]
  );

  if (!isVisible) return null;

  const handleDismiss = (
    actionName = "dismiss_button" | "dismiss_background"
  ) => {
    onSendPromptEvent(actionName);
    setPromptDismissed();
    setIsVisible(false);
    document.body.classList.remove("modal-open");
  };

  const handleOpenApp = () => {
    onSendPromptEvent("click");
    openAppOrStore(getAppUrl());
    setIsVisible(false);
  };

  return (
    <>
      <div
        className="modal-overlay"
        onClick={() => handleDismiss("dismiss_background")}
        role="presentation"
      />
      <div
        className={"new-mobile-download-prompt"}
        ref={drawerRefCb}
        aria-modal="true"
        role="dialog"
        aria-label="Dialog"
      >
        <Button
          className={"open-button"}
          size={ButtonSize.SMALL}
          onClick={handleOpenApp}
          gtm="Download|bottom_banner|open_app"
          label={trans("Open")}
        />
        <div className={"title"}>
          <img
            src={"/img/logos/Wattpad_Logomark_Orange_RGB.svg"}
            data-testid="wattpad-logo"
            alt={trans("Wattpad Logo")}
          />
          <span
            className={"mobile-download-prompt-logo-text"}
            data-testid="prompt-text"
          >
            {trans("View in Wattpad app")}
          </span>
        </div>
        <button
          className={"close-button"}
          name="close"
          onClick={() => handleDismiss("dismiss_button")}
        >
          <Icon name={"close"} size={24} />
        </button>
      </div>
    </>
  );
};

const APP_SCHEME = "wattpad://";
// how long we wait for the app to open before redirecting to the app store (ms)
const APP_STORE_REDIRECT_DELAY = 1000;

/**
 * helper to map current path to app deeplink
 */
const getAppDeeplink = () => {
  // default to Home
  let appPath = "Home";
  if (typeof window === "undefined") return APP_SCHEME + appPath;

  const currentPath = window.location.pathname.toLocaleLowerCase();

  try {
    const parts = currentPath.split("/");
    const root = parts[1];

    switch (root) {
      case "story": {
        const story_id = parseInt(parts[2]);
        appPath = `story/${story_id}/details`;
        break;
      }
      case "library":
      case "archive":
        appPath = "library";
        break;
      case "user": {
        const userName = parts[2];
        appPath = `user/${userName}`;
        break;
      }
      case "list": {
        const listId = parseInt(parts[2]);
        appPath = `reading-list/${listId}`;
        break;
      }
      default:
        appPath = currentPath;
        break;
    }
  } catch (error) {
    // if anything goes wrong with parsing the path, default to the current path
    appPath = currentPath;
  }

  return APP_SCHEME + appPath;
};

/**
 * Attempts to redirect to the current path's app equivalent
 * Defers to app store if user does not have app installed
 * @param appDownloadUrl link to the app download page
 */
export const openAppOrStore = appDownloadUrl => {
  const appDeeplink = getAppDeeplink();

  let hidden = false;

  const onVisibilityChange = () => {
    if (document.hidden) {
      hidden = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);

  window.location.href = appDeeplink;

  const timer = setTimeout(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    if (!hidden) {
      window.location.href = appDownloadUrl;
    }
  }, APP_STORE_REDIRECT_DELAY);
};

const SESSION_KEY = "mobile-prompt-dismissed";

const setPromptDismissed = () => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(SESSION_KEY, "1");
};

const shouldShowPrompt = () => {
  if (typeof window === "undefined") return;
  return !sessionStorage.getItem(SESSION_KEY);
};

MobileDownloadPromptNew.propTypes = {
  onSendPromptEvent: PropTypes.func,
  getAppUrl: PropTypes.func
};

export default MobileDownloadPromptNew;
